import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Loader } from "@mantine/core";
import fetchApi from "../components/Lib/api";
import useStartupStore from "../stores/startupStore";

const SuggestedTasks = ({ subcategoryId, readOnly, setShowTask }) => {
  const [suggestedTasks, setSuggestedTasks] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [isPushingToBoard, setIsPushingToBoard] = useState(false);
  const [isUpdatingSuggestions, setIsUpdatingSuggestions] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [recentlyAdded, setRecentlyAdded] = useState(new Set());
  const [noticeText, setNoticeText] = useState("");
  const [noticeProgress, setNoticeProgress] = useState(0);
  const [showNotice, setShowNotice] = useState(false);
  const noticeTimerRef = React.useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addedKeys, setAddedKeys] = useState(new Set());
  const suggestedTasksLoading = useStartupStore((s) => s.suggestedTasksLoading);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const keyFor = useCallback(
    (t) =>
      `${(t.title || "").trim().toLowerCase()}|${(t.description || "")
        .trim()
        .toLowerCase()}`,
    []
  );

  const allSelected = useMemo(
    () =>
      suggestedTasks.length > 0 &&
      suggestedTasks.every(
        (t) => addedKeys.has(keyFor(t)) || selectedTaskIds.has(t.id)
      ),
    [selectedTaskIds, suggestedTasks, addedKeys, keyFor]
  );
  const someSelected = useMemo(
    () => selectedTaskIds.size > 0 && !allSelected,
    [selectedTaskIds, allSelected]
  );

  const toggleSelectTask = useCallback(
    (taskId) => {
      const task = suggestedTasks.find((t) => t.id === taskId);
      if (task && addedKeys.has(keyFor(task))) return; // don't select added tasks
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        if (next.has(taskId)) next.delete(taskId);
        else next.add(taskId);
        return next;
      });
    },
    [suggestedTasks, addedKeys, keyFor]
  );

  const clearSelection = useCallback(() => setSelectedTaskIds(new Set()), []);

  const toggleSelectAll = useCallback(() => {
    setSelectedTaskIds((prev) => {
      if (suggestedTasks.length === 0) return new Set();
      const selectable = suggestedTasks.filter(
        (t) => !addedKeys.has(keyFor(t))
      );
      if (prev.size === selectable.length) return new Set();
      return new Set(selectable.map((t) => t.id));
    });
  }, [suggestedTasks, addedKeys, keyFor]);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!subcategoryId) return;
    try {
      setIsLoading(true);
      const res = await fetchApi(
        `/sub_categories/${subcategoryId}/suggested_tasks`,
        "GET"
      );
      if (!res.ok) {
        console.error("Failed to fetch suggestions:", res.status);
        return;
      }
      const data = await res.json();
      const withIds = Array.isArray(data?.suggested_tasks)
        ? data.suggested_tasks.map((t, idx) => ({
            id: `${t.title}-${idx}`,
            title: t.title,
            description: t.description,
            index: idx,
            added: !!t.added,
          }))
        : [];
      setSuggestedTasks(withIds);
      // Initialize added state from backend
      const serverAdded = withIds.filter((t) => t.added);
      if (serverAdded.length > 0) {
        setAddedKeys(new Set(serverAdded.map((t) => keyFor(t))));
      } else {
        setAddedKeys(new Set());
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setIsLoading(false);
    }
  }, [subcategoryId, keyFor, suggestedTasksLoading]);

  // Fetch suggestions on mount and when subcategoryId changes
  useEffect(() => {
    fetchSuggestions();
    // reset local added markers on subcategory change
    setAddedKeys(new Set());
    setRecentlyAdded(new Set());
  }, [fetchSuggestions]);

  // Listen for cross-app refresh signals
  useEffect(() => {
    let interval = null;
    let cancelled = false;

    async function attemptRefreshWithRetries() {
      // immediate refresh
      await fetchSuggestions();
      if (cancelled) return;
      // If still empty, retry a few times
      if (suggestedTasks.length === 0) {
        let retries = 0;
        const maxRetries = 5; // ~10s total with 2s interval
        interval = setInterval(async () => {
          retries += 1;
          await fetchSuggestions();
          if (cancelled) {
            clearInterval(interval);
            interval = null;
            return;
          }
          // Use latest state by checking set state updater
          setSuggestedTasks((current) => {
            if (current.length > 0 || retries >= maxRetries) {
              if (interval) clearInterval(interval);
              interval = null;
            }
            return current;
          });
        }, 2000);
      }
    }

    function onSuggestionsUpdated(e) {
      const changedId = e?.detail?.subcategoryId;
      if (!changedId || changedId !== subcategoryId) return;
      attemptRefreshWithRetries();
    }

    function onProgramUpdated() {
      attemptRefreshWithRetries();
    }

    window.addEventListener(
      "subcategory:suggested_tasks_updated",
      onSuggestionsUpdated
    );
    window.addEventListener("program:updated", onProgramUpdated);

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      interval = null;
      window.removeEventListener(
        "subcategory:suggested_tasks_updated",
        onSuggestionsUpdated
      );
      window.removeEventListener("program:updated", onProgramUpdated);
    };
  }, [fetchSuggestions, subcategoryId, suggestedTasks.length]);

  const triggerHeaderNotice = useCallback((text, durationMs = 5000) => {
    setNoticeText(text);
    setShowNotice(true);
    setNoticeProgress(100);
    if (noticeTimerRef.current) {
      clearInterval(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
    const start = Date.now();
    noticeTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / durationMs) * 100);
      setNoticeProgress(pct);
      if (elapsed >= durationMs) {
        clearInterval(noticeTimerRef.current);
        noticeTimerRef.current = null;
        setShowNotice(false);
      }
    }, 100);
  }, []);

  const closeNotice = useCallback(() => {
    if (noticeTimerRef.current) {
      clearInterval(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
    setShowNotice(false);
  }, []);

  const pushTasksToTaskboard = useCallback(
    async (tasks) => {
      try {
        if (!subcategoryId || !Array.isArray(tasks) || tasks.length === 0)
          return;
        setIsPushingToBoard(true);
        const res = await fetchApi(
          `/sub_categories/${subcategoryId}/task_boards/add_suggested_tasks`,
          "POST",
          { tasks }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          console.error("Failed to push tasks to board", data);
        }
      } catch (err) {
        console.error("Error pushing tasks to board", err);
      } finally {
        setIsPushingToBoard(false);
      }
    },
    [subcategoryId]
  );

  // Backend marking now happens inside add_suggested_tasks; no extra call needed

  // Preserve suggestions: no-op removal function kept for compatibility
  const removeSuggestionsOnServer = useCallback(async (_tasks) => {
    return;
  }, []);

  const removeTask = useCallback(
    (taskId) => {
      setSuggestedTasks((prev) => prev.filter((t) => t.id !== taskId));
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      const task = suggestedTasks.find((t) => t.id === taskId);
      if (task && subcategoryId) {
        // If user explicitly removes, it's fine to call server removal
        removeSuggestionsOnServer([
          { title: task.title, description: task.description },
        ]);
      }
    },
    [removeSuggestionsOnServer, subcategoryId, suggestedTasks]
  );

  const addSingleTask = useCallback(
    (task) => {
      if (readOnly) return;
      if (addedKeys.has(keyFor(task))) return;
      pushTasksToTaskboard([
        { title: task.title, description: task.description, index: task.index },
      ]);
      // Mark as recently added, keep card visible for a short confirmation window
      setRecentlyAdded((prev) => new Set(prev).add(task.id));
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
      // Add keys for both current and cleared-description variants to survive refetch
      setAddedKeys((prev) => {
        const next = new Set(prev);
        next.add(keyFor(task));
        next.add(`${(task.title || "").trim().toLowerCase()}|`);
        return next;
      });
      // Clear description and flag as added locally
      setSuggestedTasks((prev) =>
        prev.map((t) =>
          t.id === task.id ? { ...t, added: true, description: "" } : t
        )
      );
      triggerHeaderNotice("Moving 1 task to the taskboard");
    },
    [pushTasksToTaskboard, triggerHeaderNotice, readOnly, addedKeys, keyFor]
  );

  const addSelectedTasks = useCallback(async () => {
    if (readOnly) return;
    const tasks = suggestedTasks.filter(
      (t) => selectedTaskIds.has(t.id) && !addedKeys.has(keyFor(t))
    );
    if (!tasks.length) return;
    setSelectedTaskIds(new Set());

    let movedCount = 0;
    for (const task of tasks) {
      const payload = [
        { title: task.title, description: task.description, index: task.index },
      ];
      try {
        await pushTasksToTaskboard(payload);
      } catch (_) {}
      // Mark as added locally; keep visible but disable re-add
      setAddedKeys((prev) => {
        const next = new Set(prev);
        next.add(keyFor(task));
        next.add(`${(task.title || "").trim().toLowerCase()}|`);
        return next;
      });
      setRecentlyAdded((prev) => new Set(prev).add(task.id));
      // Clear description and flag as added locally
      setSuggestedTasks((prev) =>
        prev.map((t) =>
          t.id === task.id ? { ...t, added: true, description: "" } : t
        )
      );
      movedCount += 1;
    }

    triggerHeaderNotice(
      `Moving ${movedCount} task${movedCount > 1 ? "s" : ""} to the taskboard`
    );
  }, [
    pushTasksToTaskboard,
    triggerHeaderNotice,
    readOnly,
    selectedTaskIds,
    suggestedTasks,
    addedKeys,
    keyFor,
  ]);

  // Hide when empty unless store indicates tasks are being prepared
  if (suggestedTasks.length === 0) {
    if (!suggestedTasksLoading) return null;
    return (
      <div
        className="antialiased"
        style={{
          marginTop: "12px",
          background: "#ffffff",
          border: "1px solid #eaeaea",
          borderRadius: "8px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          padding: "12px",
        }}
        aria-busy={true}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="text-slate-700 font-semibold">Suggested Tasks</div>
            <div
              className="text-slate-600 bg-slate-100"
              style={{
                fontSize: "12px",
                padding: "2px 8px",
                borderRadius: 9999,
              }}
            >
              —
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "#6b7280",
            fontSize: "0.9rem",
          }}
        >
          <Loader size="xs" color="#6c45e7" />
          <span>Preparing suggestions…</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="antialiased"
      style={{
        marginTop: "12px",
        background: "#ffffff",
        border: "1px solid #eaeaea",
        borderRadius: "8px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        padding: "12px",
      }}
      aria-busy={isLoading}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          gap: isMobile ? "12px" : "0px",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}
        >
          <div className="text-slate-700 font-semibold">Suggested Tasks</div>
          <div
            className="text-slate-600 bg-slate-100"
            style={{ fontSize: "12px", padding: "2px 8px", borderRadius: 9999 }}
          >
            {isLoading ? "—" : suggestedTasks.length}
          </div>
          {isLoading && suggestedTasks.length > 0 && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#6b7280",
                fontSize: "0.85rem",
              }}
            >
              <Loader size="xs" color="#6c45e7" />
              <span>Refreshing…</span>
            </div>
          )}
        </div>
        {showNotice ? (
          <div
            style={{
              flex: 1,
              marginLeft: 10,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
            role="status"
            aria-live="polite"
          >
            {isPushingToBoard || isUpdatingSuggestions ? (
              <>
                <Loader size="xs" color="#6c45e7" />
                <div
                  className="text-slate-700"
                  style={{ fontSize: "0.95rem", fontWeight: 600 }}
                >
                  {isPushingToBoard
                    ? "Moving tasks to the taskboard…"
                    : "Updating suggestions…"}
                </div>
                <Button size="xs" variant="light" disabled>
                  Opening…
                </Button>
                <button
                  type="button"
                  onClick={closeNotice}
                  aria-label="Close notification"
                  style={{
                    marginLeft: "auto",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#6b7280",
                    fontSize: 14,
                  }}
                >
                  ×
                </button>
              </>
            ) : (
              <>
                <div
                  className="text-slate-700"
                  style={{ fontSize: "0.95rem", fontWeight: 600 }}
                >
                  {noticeText}
                </div>
                <div
                  style={{
                    flex: "0 0 180px",
                    width: 180,
                    height: 6,
                    background: "#e5e7eb",
                    borderRadius: 9999,
                  }}
                >
                  <div
                    style={{
                      height: 6,
                      width: `${noticeProgress}%`,
                      background: "#6c45e7",
                      borderRadius: 9999,
                      transition: "width 100ms linear",
                    }}
                  />
                </div>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    if (typeof setShowTask === "function") setShowTask(true);
                  }}
                >
                  Open taskboard
                </Button>
                <button
                  type="button"
                  onClick={closeNotice}
                  aria-label="Close notification"
                  style={{
                    marginLeft: "auto",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#6b7280",
                    fontSize: 14,
                  }}
                >
                  ×
                </button>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Checkbox
              size="xs"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleSelectAll}
              disabled={isLoading || readOnly || suggestedTasks.length === 0}
              label="Select all"
            />
            <Button
              size="xs"
              variant="default"
              onClick={clearSelection}
              disabled={
                isLoading ||
                readOnly ||
                selectedTaskIds.size === 0 ||
                isPushingToBoard ||
                isUpdatingSuggestions
              }
            >
              Clear
            </Button>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setIsCollapsed((c) => !c)}
              disabled={isLoading}
            >
              {isCollapsed ? "Expand" : "Collapse"}
            </Button>
            <Button
              size="xs"
              variant="light"
              color="#6c45e7"
              onClick={addSelectedTasks}
              disabled={
                isLoading ||
                readOnly ||
                selectedTaskIds.size === 0 ||
                isPushingToBoard ||
                isUpdatingSuggestions
              }
            >
              {isPushingToBoard
                ? "Adding..."
                : isUpdatingSuggestions
                ? "Updating..."
                : "Add Selected"}
            </Button>
          </div>
        )}
      </div>

      {!isCollapsed && suggestedTasks.length > 0 && (
        <div
          className="text-slate-600"
          style={{ fontSize: "0.9rem", marginBottom: 10 }}
        >
          Select one or more suggestions to quickly insert them into your
          taskboard
        </div>
      )}

      {isCollapsed ? null : suggestedTasks.length === 0 ? (
        isLoading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#6b7280",
              fontSize: "0.9rem",
            }}
          >
            <Loader size="xs" color="#6c45e7" />
            <span>Loading…</span>
          </div>
        ) : null
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${isMobile ? "1fr" : "repeat(2, 1fr)"}`,
            gap: "12px",
          }}
        >
          {suggestedTasks.map((task) => {
            const added = addedKeys.has(keyFor(task));
            return (
              <div
                key={task.id}
                style={{
                  border: "1px solid #eaeaea",
                  borderRadius: 8,
                  padding: 12,
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  transition: "box-shadow 120ms ease, border-color 120ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(0,0,0,0.06)";
                  e.currentTarget.style.borderColor = "#e3e3e3";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.04)";
                  e.currentTarget.style.borderColor = "#eaeaea";
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 8 }}
                >
                  <Checkbox
                    size="xs"
                    checked={selectedTaskIds.has(task.id)}
                    onChange={() => toggleSelectTask(task.id)}
                    disabled={readOnly || added}
                    aria-label={`Select ${task.title}`}
                    styles={{ input: { marginTop: 3 } }}
                  />
                  <div>
                    <div
                      className="text-slate-700"
                      style={{ fontWeight: 600, lineHeight: 1.2 }}
                    >
                      {task.title}
                    </div>
                    <div
                      className="text-slate-600"
                      style={{ fontSize: "0.9rem", marginTop: 4 }}
                    >
                      {task.description}
                    </div>
                  </div>
                </div>
                {added ? (
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.75rem",
                          background: "#ecfdf5",
                          color: "#047857",
                          border: "1px solid #a7f3d0",
                          padding: "2px 8px",
                          borderRadius: 9999,
                          fontWeight: 600,
                        }}
                      >
                        Already added
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Button
                        size="xs"
                        variant="filled"
                        color="#6c45e7"
                        onClick={() => {
                          if (typeof setShowTask === "function")
                            setShowTask(true);
                        }}
                      >
                        View on taskboard
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => removeTask(task.id)}
                      disabled={readOnly}
                    >
                      Remove
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="#6c45e7"
                      onClick={() => addSingleTask(task)}
                      disabled={readOnly}
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default React.memo(SuggestedTasks);


